import { readModusInputChangeValue } from '../../utils/modus-input-change';
import {
  Component,
  JSXElement,
  createSignal,
  onMount,
  onCleanup,
  createEffect,
  untrack,
} from 'solid-js';
import tinycolor from 'tinycolor2';
import Button from '../Button/Button';
import { BtnClasses } from '../../types/common';
import './ColorPicker.css';

export interface HSLColor {
  h: number;
  s: number;
  l: number;
  a?: number;
}

export interface RGBColor {
  r: number;
  g: number;
  b: number;
  a?: number;
}

export interface HSVColor {
  a?: number;
  h: number;
  s: number;
  v: number;
}

export interface ColorPickerProps {
  selectedColor?: string;
  onClose(): void;
  onSave(hex: string, hsl?: HSLColor, rgb?: RGBColor, hsv?: HSVColor): void;
  onChange?(rgb: RGBColor): void;
}

const HUE_DEGREES = 360;
const MAX_RGB_CHANNEL = 255;
const PERCENT = 100;
const DECIMAL_RADIX = 10;

const SAT_CANVAS_WIDTH = 240;
const SAT_CANVAS_HEIGHT = 180;
const HUE_CANVAS_WIDTH = 200;
const HUE_CANVAS_HEIGHT = 10;

// Hue-gradient color stops (fractions of 0 to 1 across the full spectrum)
const HUE_STOP_YELLOW = 0.17;
const HUE_STOP_GREEN = 0.33;
const HUE_STOP_CYAN = 0.5;
const HUE_STOP_BLUE = 0.67;
const HUE_STOP_MAGENTA = 0.83;

function concatHash(hex: string): string {
  return hex[0] !== '#' ? '#' + hex : hex;
}

const ColorPicker: Component<ColorPickerProps> = (props): JSXElement => {
  let satRef: HTMLCanvasElement | undefined;
  let hueRef: HTMLCanvasElement | undefined;

  const initialColor = tinycolor(
    untrack(() => props.selectedColor) || '#aaaaaa'
  );

  const [hex, setHex] = createSignal(initialColor.toHex());
  const [hsl, setHsl] = createSignal<HSLColor>(initialColor.toHsl());
  const [hsv, setHsv] = createSignal<HSVColor>(initialColor.toHsv());
  const [rgb, setRgb] = createSignal<RGBColor>(initialColor.toRgb());

  const [draggingSat, setDraggingSat] = createSignal(false);
  const [draggingHue, setDraggingHue] = createSignal(false);

  function updateAllFromColor(color: tinycolor.Instance): void {
    setHex(color.toHex());
    setHsl(color.toHsl());
    setHsv(color.toHsv());
    setRgb(color.toRgb());
  }

  // --- Saturation canvas (replaces react-color <Saturation>) ---
  function drawSaturation(): void {
    if (!satRef) return;
    const ctx = satRef.getContext('2d');
    if (!ctx) return;
    const w = satRef.width;
    const h = satRef.height;

    const hueColor = tinycolor({ h: hsv().h, s: 1, v: 1 }).toRgbString();

    // Horizontal gradient: white (left) ? pure hue color (right)
    const hGradient = ctx.createLinearGradient(0, 0, w, 0);
    hGradient.addColorStop(0, 'white');
    hGradient.addColorStop(1, hueColor);
    ctx.fillStyle = hGradient;
    ctx.fillRect(0, 0, w, h);

    // Vertical overlay: transparent (top) ? black (bottom)
    const vGradient = ctx.createLinearGradient(0, 0, 0, h);
    vGradient.addColorStop(0, 'transparent');
    vGradient.addColorStop(1, 'black');
    ctx.fillStyle = vGradient;
    ctx.fillRect(0, 0, w, h);
  }

  function handleSatInteraction(e: MouseEvent): void {
    if (!satRef) return;
    const rect = satRef.getBoundingClientRect();
    const s = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const v = Math.max(
      0,
      Math.min(1, 1 - (e.clientY - rect.top) / rect.height)
    );
    const newColor = tinycolor({ h: hsv().h, s, v });
    updateAllFromColor(newColor);
    props.onChange?.(newColor.toRgb());
  }

  // --- Hue canvas (replaces react-color <Hue>) ---
  function drawHue(): void {
    if (!hueRef) return;
    const ctx = hueRef.getContext('2d');
    if (!ctx) return;
    const w = hueRef.width;
    const h = hueRef.height;

    const gradient = ctx.createLinearGradient(0, 0, w, 0);
    gradient.addColorStop(0, '#ff0000');
    gradient.addColorStop(HUE_STOP_YELLOW, '#ffff00');
    gradient.addColorStop(HUE_STOP_GREEN, '#00ff00');
    gradient.addColorStop(HUE_STOP_CYAN, '#00ffff');
    gradient.addColorStop(HUE_STOP_BLUE, '#0000ff');
    gradient.addColorStop(HUE_STOP_MAGENTA, '#ff00ff');
    gradient.addColorStop(1, '#ff0000');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, w, h);
  }

  function handleHueInteraction(e: MouseEvent): void {
    if (!hueRef) return;
    const rect = hueRef.getBoundingClientRect();
    const ratio = Math.max(
      0,
      Math.min(1, (e.clientX - rect.left) / rect.width)
    );
    const newHue = ratio * HUE_DEGREES;
    const currentHsv = hsv();
    const newColor = tinycolor({ h: newHue, s: currentHsv.s, v: currentHsv.v });
    setHex(newColor.toHex());
    setHsl({ ...newColor.toHsl(), h: newHue });
    setHsv({ ...newColor.toHsv(), h: newHue });
    setRgb(newColor.toRgb());
    props.onChange?.(newColor.toRgb());
    drawSaturation();
  }

  // --- Global mouse tracking for drag ---
  function handleMouseMove(e: MouseEvent): void {
    if (draggingSat()) handleSatInteraction(e);
    if (draggingHue()) handleHueInteraction(e);
  }

  function handleMouseUp(): void {
    setDraggingSat(false);
    setDraggingHue(false);
  }

  onMount(() => {
    drawSaturation();
    drawHue();
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  });

  onCleanup(() => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  });

  createEffect(() => {
    if (props.selectedColor) {
      const color = tinycolor(props.selectedColor);
      updateAllFromColor(color);
      untrack(() => {
        drawSaturation();
      });
    }
  });

  // --- Input handlers (replaces react-color <EditableInput>) ---
  function handleHexChange(e: Event): void {
    const val = readModusInputChangeValue(e);
    const clean = String(val).replace('#', '');
    if (/^[0-9a-fA-F]{3,6}$/.test(clean)) {
      const color = tinycolor(clean);
      updateAllFromColor(color);
      drawSaturation();
      props.onChange?.(color.toRgb());
    }
  }

  function handleRgbChange(channel: 'r' | 'g' | 'b', e: Event): void {
    const val = readModusInputChangeValue(e);
    const num = parseInt(String(val), DECIMAL_RADIX);
    if (isNaN(num) || num < 0 || num > MAX_RGB_CHANNEL) return;
    const newRgb = { ...rgb(), [channel]: num };
    const color = tinycolor(newRgb);
    updateAllFromColor(color);
    drawSaturation();
    props.onChange?.(newRgb);
  }

  // Saturation pointer position
  const satPointerX = (): string => hsv().s * PERCENT + '%';
  const satPointerY = (): string => (1 - hsv().v) * PERCENT + '%';
  const huePointerX = (): string => (hsv().h / HUE_DEGREES) * PERCENT + '%';

  return (
    <div class="cp-container">
      <div class="cp-palette">
        <canvas
          ref={satRef}
          width={SAT_CANVAS_WIDTH}
          height={SAT_CANVAS_HEIGHT}
          onMouseDown={(e) => {
            setDraggingSat(true);
            handleSatInteraction(e);
          }}
        />
        <div
          class="cp-pointer"
          style={{ left: satPointerX(), top: satPointerY() }}
        />
      </div>

      <div class="cp-hue-container">
        <div class="cp-hue">
          <canvas
            ref={hueRef}
            width={HUE_CANVAS_WIDTH}
            height={HUE_CANVAS_HEIGHT}
            onMouseDown={(e) => {
              setDraggingHue(true);
              handleHueInteraction(e);
            }}
          />
          <div class="cp-slider" style={{ left: huePointerX() }} />
        </div>
        <div>
          <span
            class="cp-preview"
            style={{
              'background-color': `rgb(${rgb().r}, ${rgb().g}, ${rgb().b})`,
            }}
          />
        </div>
      </div>

      <div class="cp-inputs">
        <div class="cp-input-wrap">
          <modus-wc-text-input
            value={concatHash(hex())}
            size="sm"
            on:inputChange={handleHexChange}
          />
          <span class="cp-input-label">Hex</span>
        </div>
        <div class="cp-input-wrap cp-rgb-input">
          <modus-wc-text-input
            value={String(rgb().r)}
            size="sm"
            on:inputChange={(e: Event) => {
              handleRgbChange('r', e);
            }}
          />
          <span class="cp-input-label">R</span>
        </div>
        <div class="cp-input-wrap cp-rgb-input">
          <modus-wc-text-input
            value={String(rgb().g)}
            size="sm"
            on:inputChange={(e: Event) => {
              handleRgbChange('g', e);
            }}
          />
          <span class="cp-input-label">G</span>
        </div>
        <div class="cp-input-wrap cp-rgb-input">
          <modus-wc-text-input
            value={String(rgb().b)}
            size="sm"
            on:inputChange={(e: Event) => {
              handleRgbChange('b', e);
            }}
          />
          <span class="cp-input-label">B</span>
        </div>
      </div>

      <div class="cp-buttons">
        <Button
          color={BtnClasses.Secondary}
          variant="borderless"
          size="small"
          content="Cancel"
          onClick={() => {
            props.onClose();
          }}
        />
        <Button
          color={BtnClasses.Primary}
          size="small"
          content="Save"
          onClick={() => {
            props.onSave(concatHash(hex()), hsl(), rgb(), hsv());
          }}
        />
      </div>
    </div>
  );
};

export default ColorPicker;
